// frontend/src/components/Dashboard/SalesChart.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faCalendarAlt,
  faArrowTrendUp,
  faArrowTrendDown,
  faInfoCircle,
  faGlobe,
  faShip,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";

// ============================================
// BRAND & SUPPLIER CONSTANTS
// ============================================
const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

const SalesChart = ({ data, onPeriodChange, title = "Sales Overview" }) => {
  const [period, setPeriod] = useState("30d");
  const [hoveredBar, setHoveredBar] = useState(null);

  const chartData =
    data && data.length > 0
      ? data
      : [
          { day: "Mon", sales: 120, malaysia: 80 },
          { day: "Tue", sales: 180, malaysia: 120 },
          { day: "Wed", sales: 150, malaysia: 100 },
          { day: "Thu", sales: 220, malaysia: 160 },
          { day: "Fri", sales: 280, malaysia: 200 },
          { day: "Sat", sales: 200, malaysia: 140 },
          { day: "Sun", sales: 160, malaysia: 110 },
        ];

  const maxValue = Math.max(...chartData.map((d) => d.sales), 1);
  const totalSales = chartData.reduce((sum, d) => sum + d.sales, 0);
  const totalMalaysia = chartData.reduce(
    (sum, d) => sum + (d.malaysia || 0),
    0,
  );
  const malaysiaPercentage =
    totalSales > 0 ? (totalMalaysia / totalSales) * 100 : 0;
  const avgSales = totalSales / chartData.length;
  const trend =
    chartData.length > 1
      ? chartData[chartData.length - 1].sales - chartData[0].sales
      : 0;

  const handlePeriodChange = (e) => {
    const newPeriod = e.target.value;
    setPeriod(newPeriod);
    if (onPeriodChange) onPeriodChange(newPeriod);
  };

  const formatCurrency = (amount) => `$${amount.toLocaleString()}`;

  return (
    <div className="sales-chart">
      <div className="chart-header">
        <div className="chart-title-section">
          <FontAwesomeIcon icon={faChartLine} className="chart-icon" />
          <h3>{title}</h3>
          <span className="supplier-badge">
            <FontAwesomeIcon icon={faShip} /> {SUPPLIER.country}
          </span>
        </div>
        <div className="chart-controls">
          <div className="chart-stats">
            <span className="chart-stat">
              <strong>Total:</strong> {formatCurrency(totalSales)}
            </span>
            <span className="chart-stat">
              <strong>Avg:</strong> {formatCurrency(avgSales)}
            </span>
            <span className="chart-stat malaysia-stat">
              <FontAwesomeIcon icon={faGlobe} /> {malaysiaPercentage.toFixed(0)}
              % from {SUPPLIER.country}
            </span>
            <span
              className={`chart-trend ${trend >= 0 ? "positive" : "negative"}`}
            >
              <FontAwesomeIcon
                icon={trend >= 0 ? faArrowTrendUp : faArrowTrendDown}
              />
              {trend >= 0 ? "+" : ""}
              {formatCurrency(trend)}
            </span>
          </div>
          <select
            className="chart-period"
            value={period}
            onChange={handlePeriodChange}
          >
            <option value="7d">Last 7 Days</option>
            <option value="14d">Last 14 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      <div className="chart-container">
        <div className="chart-bars">
          {chartData.map((item, index) => {
            const height = (item.sales / maxValue) * 100;
            const malaysiaHeight = ((item.malaysia || 0) / maxValue) * 100;
            const isHovered = hoveredBar === index;
            const dayLabel = item.day || `Day ${index + 1}`;

            return (
              <div
                key={index}
                className="chart-bar-wrapper"
                onMouseEnter={() => setHoveredBar(index)}
                onMouseLeave={() => setHoveredBar(null)}
              >
                <div
                  className="chart-bar-container"
                  style={{ height: `${Math.max(height, 5)}%` }}
                >
                  <div
                    className="chart-bar chart-bar-malaysia"
                    style={{ height: `${Math.max(malaysiaHeight, 2)}%` }}
                  />
                  <div
                    className={`chart-bar chart-bar-total ${isHovered ? "hovered" : ""}`}
                    style={{ height: `${Math.max(height, 5)}%` }}
                  >
                    {isHovered && (
                      <div className="chart-bar-tooltip">
                        <div className="tooltip-day">{dayLabel}</div>
                        <div className="tooltip-value">
                          {formatCurrency(item.sales)}
                        </div>
                        {item.malaysia !== undefined && (
                          <div className="tooltip-malaysia">
                            <FontAwesomeIcon icon={faGlobe} />{" "}
                            {formatCurrency(item.malaysia)} from{" "}
                            {SUPPLIER.country}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="chart-label">{dayLabel}</div>
                <div className="chart-value">{formatCurrency(item.sales)}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="chart-footer">
        <div className="chart-legend">
          <span className="legend-item">
            <span className="legend-color total" />
            Total Sales
          </span>
          <span className="legend-item">
            <span className="legend-color malaysia" />
            {SUPPLIER.country} Origin
          </span>
        </div>
        <div className="chart-info">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>Hover over bars for details</span>
        </div>
      </div>

      <style>{`
        .sales-chart {
          background: #ffffff;
          padding: 1.25rem 1.5rem;
          border-radius: 8px;
          border: 1px solid #e5e5e5;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .chart-title-section {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .chart-icon { color: #d9a321; font-size: 1.1rem; }
        .chart-title-section h3 { color: #006b2e; margin: 0; font-size: 1rem; font-family: "Montserrat", sans-serif; }

        .supplier-badge {
          font-size: 0.55rem;
          padding: 0.1rem 0.5rem;
          border-radius: 10px;
          background: #dcfce7;
          color: #16a34a;
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
        }

        .chart-controls {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .chart-stats {
          display: flex;
          gap: 0.75rem;
          font-size: 0.7rem;
          color: #5a5a5a;
          flex-wrap: wrap;
        }

        .chart-stat strong { color: #1f1f1f; }
        .chart-stat.malaysia-stat { color: #16a34a; background: #dcfce7; padding: 0.1rem 0.4rem; border-radius: 8px; }

        .chart-trend {
          font-weight: 600;
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          font-size: 0.7rem;
        }

        .chart-trend.positive { color: #16a34a; background: #dcfce7; }
        .chart-trend.negative { color: #dc2626; background: #fee2e2; }

        .chart-period {
          padding: 0.3rem 0.75rem;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          font-size: 0.7rem;
          background: #ffffff;
          color: #1f1f1f;
          cursor: pointer;
          transition: border-color 0.3s ease;
        }

        .chart-period:focus { outline: none; border-color: #d9a321; }

        .chart-container { height: 200px; display: flex; align-items: flex-end; padding: 0.5rem 0; }
        .chart-bars { display: flex; justify-content: space-between; align-items: flex-end; width: 100%; height: 100%; gap: 0.25rem; }

        .chart-bar-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
          height: 100%;
          position: relative;
        }

        .chart-bar-container {
          width: 100%;
          max-width: 40px;
          min-height: 6px;
          border-radius: 3px 3px 0 0;
          position: relative;
          display: flex;
          flex-direction: column-reverse;
          overflow: hidden;
        }

        .chart-bar {
          width: 100%;
          border-radius: 2px;
          transition: all 0.3s ease;
          position: relative;
          cursor: pointer;
        }

        .chart-bar-total {
          background: linear-gradient(180deg, #d9a321, #b8860b);
          min-height: 4px;
          border-radius: 2px 2px 0 0;
        }

        .chart-bar-malaysia {
          background: #006B2E;
          min-height: 2px;
          opacity: 0.6;
          border-radius: 2px 2px 0 0;
        }

        .chart-bar-total.hovered {
          transform: scaleY(1.02);
          box-shadow: 0 2px 8px rgba(217, 163, 33, 0.3);
        }

        .chart-bar-tooltip {
          position: absolute;
          top: -65px;
          left: 50%;
          transform: translateX(-50%);
          background: #1f1f1f;
          color: #ffffff;
          padding: 0.3rem 0.6rem;
          border-radius: 4px;
          font-size: 0.6rem;
          text-align: center;
          white-space: nowrap;
          pointer-events: none;
          min-width: 100px;
        }

        .chart-bar-tooltip::after {
          content: "";
          position: absolute;
          bottom: -6px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid #1f1f1f;
        }

        .tooltip-day { font-weight: 400; opacity: 0.7; }
        .tooltip-value { font-weight: 600; font-size: 0.7rem; }
        .tooltip-malaysia { font-size: 0.55rem; color: #16a34a; margin-top: 0.1rem; }

        .chart-label { font-size: 0.55rem; color: #9ca3af; margin-top: 0.25rem; text-align: center; }
        .chart-value { font-size: 0.5rem; color: #5a5a5a; margin-top: 0.1rem; text-align: center; }

        .chart-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 1rem;
          padding-top: 0.5rem;
          border-top: 1px solid #f0f0f0;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .chart-legend { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.65rem;
          color: #5a5a5a;
        }

        .legend-color {
          width: 14px;
          height: 14px;
          border-radius: 3px;
        }

        .legend-color.total { background: linear-gradient(180deg, #d9a321, #b8860b); }
        .legend-color.malaysia { background: #006B2E; }

        .chart-info {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.6rem;
          color: #9ca3af;
        }

        .chart-info svg { font-size: 0.7rem; }

        @media (max-width: 768px) {
          .sales-chart { padding: 1rem; }
          .chart-header { flex-direction: column; align-items: stretch; }
          .chart-controls { flex-direction: column; align-items: stretch; }
          .chart-stats { flex-wrap: wrap; }
          .chart-bar-container { max-width: 30px; }
          .chart-container { height: 150px; }
        }

        @media (max-width: 480px) {
          .chart-bar-container { max-width: 20px; min-height: 4px; }
          .chart-container { height: 120px; }
          .chart-label { font-size: 0.45rem; }
          .chart-value { font-size: 0.4rem; }
          .chart-stats { font-size: 0.6rem; }
          .chart-bar-tooltip { font-size: 0.5rem; min-width: 80px; top: -55px; }
        }
      `}</style>
    </div>
  );
};

export default SalesChart;
